import { getDistance } from '@m1/geo-utils';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import commaNumber from 'comma-number';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

import Term from './Term';
import styles from './Weather.module.scss';

import Bar from '@/components/ui/Bar';
import API from '@/helpers/API';

const cx = classNames.bind(styles);

const DISTANCE_THRESHOLD = 1000; // 위치 변경 시 fetch 기준 거리 (1km)
const DIRECTIONS = [
  { name: 'N', value: 0 },
  { name: 'NNE', value: 22.5 },
  { name: 'NE', value: 45 },
  { name: 'ENE', value: 67.5 },
  { name: 'E', value: 90 },
  { name: 'ESE', value: 112.5 },
  { name: 'SE', value: 135 },
  { name: 'SSE', value: 157.5 },
  { name: 'S', value: 180 },
  { name: 'SSW', value: 202.5 },
  { name: 'SW', value: 225 },
  { name: 'WSW', value: 247.5 },
  { name: 'W', value: 270 },
  { name: 'WNW', value: 292.5 },
  { name: 'NW', value: 315 },
  { name: 'NNW', value: 337.5 },
];

const Weather = () => {
  const position = useSelector((state) => state.map.position);
  const [data, setData] = useState();
  const [isKelvinScale, setKelvinScale] = useState(false);
  const [isOpenWindDirection, setIsOpenWindDirection] = useState(false);
  const accumulatedDistance = useRef(0); // 위치 이동 누적 거리
  const windDirectionTermRef = useRef();

  // 날씨 정보 fetch 필요 여부
  const needToFetch = useMemo(() => {
    if (!data) return true;

    // 현재 위치와 이전 날씨 정보의 위치 간 거리 누적
    accumulatedDistance.current += getDistance(data.position, position);
    // 누적 거리 임계값 초과 여부
    return accumulatedDistance.current > DISTANCE_THRESHOLD;
  }, [position, data]);

  useEffect(() => {
    if (!needToFetch) return;

    API.get(`/proxy/weather?lat=${position.lat}&lon=${position.lng}`)
      .then((result) => {
        if (result.success) {
          // 누적 거리 초기화
          accumulatedDistance.current = 0;
          setData({ ...result.data, position });
        }
      })
      .catch(() => setData());
  }, [needToFetch]);

  useUpdateEffect(() => {
    if (isOpenWindDirection) {
      document.addEventListener('mousedown', unfocusPanel);
    }

    return () => {
      document.removeEventListener('mousedown', unfocusPanel);
    };
  }, [isOpenWindDirection]);

  const unfocusPanel = (e) => {
    if (windDirectionTermRef.current.contains(e.target)) return;

    setIsOpenWindDirection(false);
  };

  const toggleTemperatureScale = () => {
    setKelvinScale(!isKelvinScale);
  };

  const toggleWindDirection = (e) => {
    // Panel 클릭된 경우 이벤트 방지
    const closest = e.target.closest('[data-panel]');
    if (!closest) {
      setIsOpenWindDirection(!isOpenWindDirection);
    }
  };

  return (
    <div className={cx('container')}>
      {/* 날씨 */}
      <div className={cx('weather')}>
        {data && (
          <img
            src={`/assets/openweather/${data.weather[0].icon}@2x.png`}
            alt={data.weather[0].main}
            className={cx('icon')}
            title={data.weather[0].main}
          />
        )}
      </div>
      <Bar size={28} />
      {/* 온도 */}
      <Term
        label="Temperature"
        value={data && (data.main.temp - (isKelvinScale ? 0 : 273.15)).toFixed(1)}
        unit={isKelvinScale ? 'K' : '°C'}
        onClick={toggleTemperatureScale}
      />
      <Bar size={28} />
      {/* 습도 */}
      <Term label="Humidity" value={data?.main.humidity} unit="%" />
      <Bar size={28} />
      {/* 풍속 */}
      <Term label="Wind Speed" value={data?.wind.speed} unit="m/s" />
      <Bar size={28} />
      {/* 풍향 */}
      <Term
        ref={windDirectionTermRef}
        label="Wind Direction"
        value={data?.wind.deg}
        unit="°"
        onClick={toggleWindDirection}>
        <div data-panel className={cx(['panel', { show: isOpenWindDirection }])}>
          <div className={cx('header')}>
            <div className={cx('title')}>Wind Direction</div>
          </div>
          <div className={cx('body')}>
            <div className={cx('wrapper')}>
              {DIRECTIONS.map((direction, index) => (
                <div key={index} className={cx('step')} style={{ transform: `rotate(${direction.value}deg)` }}>
                  <div className={cx('name')}>{direction.name}</div>
                  <div className={cx('value')}>{direction.value}°</div>
                </div>
              ))}
              <div className={cx('line')} style={{ transform: 'rotate(0deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(22.5deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(45deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(67.5deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(90deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(112.5deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(135deg)' }} />
              <div className={cx('line')} style={{ transform: 'rotate(157.5deg)' }} />
              {data && (
                <>
                  <div className={cx('arrow')} style={{ transform: `rotate(${data.wind.deg}deg)` }}>
                    <div className={cx('head')} />
                  </div>
                  <div className={cx('label')}>{data.wind.deg}°</div>
                </>
              )}
            </div>
          </div>
        </div>
      </Term>
      <Bar size={28} />
      {/* 기압 */}
      <div className={cx('pressureSection')} data-hide-priority={4}>
        <Term label="Pressure" value={commaNumber(data?.main.pressure)} unit="hPa" />
        <Bar size={28} />
      </div>
    </div>
  );
};

export default Weather;
